import { Button } from '@heidi/design-system/button';
import { cn } from '@heidi/utils';
import { Loader2 } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  searching: {
    defaultMessage: 'Searching for computers…',
    id: 'DR2pD6kYFM',
    description: 'Status shown in place of the computer picker while the roster loads',
  },
  loadFailed: {
    defaultMessage: 'Couldn’t load computers',
    id: 'T41x/e6OIK',
    description: 'Computer picker recovery copy when the roster request fails',
  },
  retry: {
    defaultMessage: 'Retry',
    id: 'iOsX+WfOwC',
    description: 'Action to retry loading the computer picker roster',
  },
});

const STATUS_CLASS =
  'flex h-6 min-w-0 items-center gap-1 px-1.5 text-xs text-text-tertiary';

// Hidden through the delay, so a quick roster check never flashes the row.
const SEARCHING_ENTER =
  'animate-in fade-in-0 fill-mode-both delay-500 duration-200 ease-out';

export function DesktopTargetRosterStatus({
  status,
  onRetry,
  className,
}: {
  status: 'loading' | 'failed';
  onRetry: () => void;
  className?: string;
}): JSX.Element {
  const intl = useIntl();

  if (status === 'loading') {
    return (
      <span
        role="status"
        data-testid="desktop-target-searching"
        className={cn(STATUS_CLASS, SEARCHING_ENTER, className)}
      >
        <Loader2
          className="size-3.5 shrink-0 animate-spin motion-reduce:animate-none"
          aria-hidden
        />
        <span className="truncate">{intl.formatMessage(messages.searching)}</span>
      </span>
    );
  }

  return (
    <span
      data-testid="desktop-target-load-failed"
      className={cn(STATUS_CLASS, className)}
    >
      <span className="truncate">{intl.formatMessage(messages.loadFailed)}</span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disableAutomaticTypographyClasses
        data-testid="desktop-target-reload"
        onClick={onRetry}
        className="h-6 rounded-md px-1.5 text-xs font-normal text-text-secondary hover:bg-surface-hover hover:text-text-primary"
      >
        {intl.formatMessage(messages.retry)}
      </Button>
    </span>
  );
}
